import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import {
  ArrowLeft, DollarSign, TrendingUp, TrendingDown, Users, CreditCard,
  Calendar, RefreshCw, Download, Filter, Search, ChevronUp, ChevronDown,
  CheckCircle, XCircle, Clock, Crown, Zap
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceAdmin = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState({});
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [currentUser, authLoading, navigate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('notimax_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = async () => {
    try {
      const [summaryRes, paymentsRes, subsRes, topRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/summary`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/finance/payments`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/finance/subscriptions`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/finance/top-customers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/finance/revenue-by-month`, { headers: getAuthHeader() })
      ]);
      
      setSummary(summaryRes.data);
      setPayments(paymentsRes.data);
      setSubscriptions(subsRes.data);
      setTopCustomers(topRes.data);
      setRevenueByMonth(monthlyRes.data);
    } catch (error) {
      console.error('Error loading finance data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos financieros',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'active':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> {status === 'completed' ? 'Completado' : 'Activo'}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Pendiente</Badge>;
      case 'failed':
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> {status === 'failed' ? 'Fallido' : 'Cancelado'}</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-700"><Zap className="w-3 h-3 mr-1" /> Prueba</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanBadge = (plan) => {
    if (plan?.includes('annual') || plan === 'annual') {
      return <Badge className="bg-purple-100 text-purple-700"><Crown className="w-3 h-3 mr-1" /> Anual</Badge>;
    } else if (plan?.includes('monthly') || plan === 'monthly') {
      return <Badge className="bg-blue-100 text-blue-700"><Calendar className="w-3 h-3 mr-1" /> Mensual</Badge>;
    } else if (plan === 'trial') {
      return <Badge className="bg-orange-100 text-orange-700"><Zap className="w-3 h-3 mr-1" /> Trial</Badge>;
    }
    return <Badge variant="outline">{plan}</Badge>;
  };

  const filteredPayments = payments.filter(p => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterPlan && p.plan !== filterPlan) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        p.user_name?.toLowerCase().includes(search) ||
        p.user_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const filteredSubscriptions = subscriptions.filter(s => {
    if (filterStatus && s.status !== filterStatus) return false;
    if (filterPlan && !s.plan?.includes(filterPlan)) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        s.user_name?.toLowerCase().includes(search) ||
        s.user_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                  Finanzas
                </h1>
                <p className="text-sm text-gray-500">Control de pagos y suscripciones</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Ingresos Totales</p>
                  <p className="text-3xl font-bold">{formatCurrency(summary.total_revenue)}</p>
                  <p className="text-green-200 text-xs mt-1">{summary.total_payments || 0} pagos completados</p>
                </div>
                <DollarSign className="w-12 h-12 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Este Mes</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.this_month_revenue)}</p>
                  <div className="flex items-center mt-1">
                    {summary.growth_percentage >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={`text-xs ${summary.growth_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {summary.growth_percentage > 0 ? '+' : ''}{summary.growth_percentage}% vs mes anterior
                    </span>
                  </div>
                </div>
                <Calendar className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Suscripciones Activas</p>
                  <p className="text-3xl font-bold text-gray-900">{summary.active_subscriptions || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">{summary.trial_subscriptions || 0} en prueba</p>
                </div>
                <Users className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Por Plan</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <div>
                      <p className="text-lg font-bold text-blue-600">{summary.monthly_subscribers || 0}</p>
                      <p className="text-xs text-gray-400">Mensual</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-600">{summary.annual_subscribers || 0}</p>
                      <p className="text-xs text-gray-400">Anual</p>
                    </div>
                  </div>
                </div>
                <CreditCard className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Resumen', icon: TrendingUp },
            { id: 'payments', label: 'Pagos', icon: CreditCard },
            { id: 'subscriptions', label: 'Suscripciones', icon: Users },
            { id: 'customers', label: 'Top Clientes', icon: Crown }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className="whitespace-nowrap"
              >
                <Icon className="w-4 h-4 mr-2" /> {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Ingresos por Mes</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueByMonth.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay datos aún</p>
                ) : (
                  <div className="space-y-3">
                    {revenueByMonth.map((month, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{month.month}</p>
                          <p className="text-xs text-gray-500">{month.count} pagos</p>
                        </div>
                        <p className="font-bold text-green-600">{formatCurrency(month.revenue)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Distribución de Ingresos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-blue-700">Plan Mensual</span>
                      <span className="font-bold text-blue-700">{formatCurrency(summary.monthly_revenue)}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${summary.total_revenue ? (summary.monthly_revenue / summary.total_revenue) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">{summary.monthly_subscribers || 0} suscriptores</p>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-purple-700">Plan Anual</span>
                      <span className="font-bold text-purple-700">{formatCurrency(summary.annual_revenue)}</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${summary.total_revenue ? (summary.annual_revenue / summary.total_revenue) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">{summary.annual_subscribers || 0} suscriptores</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle>Historial de Pagos</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Todos los estados</option>
                    <option value="completed">Completado</option>
                    <option value="pending">Pendiente</option>
                    <option value="failed">Fallido</option>
                  </select>
                  <select
                    value={filterPlan}
                    onChange={(e) => setFilterPlan(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Todos los planes</option>
                    <option value="monthly">Mensual</option>
                    <option value="annual">Anual</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredPayments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay pagos registrados</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Usuario</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Plan</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Monto</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{payment.user_name}</p>
                            <p className="text-xs text-gray-500">{payment.user_email}</p>
                          </td>
                          <td className="py-3 px-4">{getPlanBadge(payment.plan)}</td>
                          <td className="py-3 px-4 font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                          <td className="py-3 px-4">{getStatusBadge(payment.status)}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">{formatDate(payment.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle>Suscripciones</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Todos los estados</option>
                    <option value="active">Activo</option>
                    <option value="trial">Prueba</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredSubscriptions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay suscripciones</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Usuario</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Plan</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Estado</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Vence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubscriptions.map((sub, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{sub.user_name}</p>
                            <p className="text-xs text-gray-500">{sub.user_email}</p>
                          </td>
                          <td className="py-3 px-4">{getPlanBadge(sub.plan)}</td>
                          <td className="py-3 px-4">{getStatusBadge(sub.status)}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">{formatDate(sub.current_period_end)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Top Customers Tab */}
        {activeTab === 'customers' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                Top Clientes
              </CardTitle>
              <CardDescription>Clientes con mayor valor de pago</CardDescription>
            </CardHeader>
            <CardContent>
              {topCustomers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay datos aún</p>
              ) : (
                <div className="space-y-4">
                  {topCustomers.map((customer, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold">{customer.name}</p>
                          <p className="text-sm text-gray-500">{customer.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600 text-lg">{formatCurrency(customer.total_paid)}</p>
                        <p className="text-xs text-gray-500">{customer.payment_count} pagos</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FinanceAdmin;
